/**
 * Runtime Environment Configuration
 *
 * Reads from window.__ENV__ (injected at container startup via config.js)
 * with fallback to import.meta.env (for local Vite dev server).
 *
 * This allows Helm to control frontend env vars at deploy time
 * without rebuilding the Docker image.
 */

interface RuntimeEnv {
  VITE_API_BASE_URL: string;
  VITE_API_URL: string;
  VITE_ENV: string;
  VITE_AUTH_MODE: string;
  VITE_ENTRA_CLIENT_ID: string;
  VITE_ENTRA_TENANT_ID: string;
  VITE_ENTRA_REDIRECT_URI: string;
}

declare global {
  interface Window {
    __ENV__?: Partial<RuntimeEnv>;
  }
}

function get(key: keyof RuntimeEnv, fallback: string = ''): string {
  // 1. Runtime config from container entrypoint (production/deployed)
  if (window.__ENV__?.[key]) {
    return window.__ENV__[key]!;
  }
  // 2. Vite build-time env (local dev)
  const viteVal = (import.meta.env as Record<string, string | undefined>)[key];
  if (viteVal) {
    return viteVal;
  }
  // 3. Fallback
  return fallback;
}

export const env = {
  /** Base URL for API calls, e.g. https://dev-api.holdrequestapp.aws.outfront.com/api */
  API_BASE_URL: get('VITE_API_BASE_URL', '/api'),

  /** WebSocket / backend origin, e.g. https://dev-api.holdrequestapp.aws.outfront.com */
  API_URL: get('VITE_API_URL', 'http://localhost:3001'),

  /** Environment name: development | staging | production */
  ENV: get('VITE_ENV', 'development'),

  /** Auth mode: mock | entra */
  AUTH_MODE: get('VITE_AUTH_MODE', 'mock'),

  /** Entra ID config */
  ENTRA_CLIENT_ID: get('VITE_ENTRA_CLIENT_ID', ''),
  ENTRA_TENANT_ID: get('VITE_ENTRA_TENANT_ID', 'common'),
  ENTRA_REDIRECT_URI: get('VITE_ENTRA_REDIRECT_URI', window.location.origin + '/auth/callback'),

  /** Convenience flags */
  get isDev() { return import.meta.env.DEV; },
  get isProd() { return import.meta.env.PROD; },
};
